import { useRef, useState } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { Replay as ReplayIcon } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import type { ApiError } from '../../utils/apiClient';
import { useModelConfigs } from './useModelConfigs';

interface ModelReplayPanelProps {
  investigationId: string;
  // Called after a replay completes so the comparison panel refetches and the new run appears.
  onReplayed?: () => void;
}

// "Replay with model" control (sub-plan 20). A model-config picker filtered to active
// role='investigation' configs + a Replay button that POSTs the NON-streaming replay (sub-plan 17-A)
// and, on success, asks the comparison panel to refetch. Gated upstream by the manage permission
// (the detail only mounts this when canManage); this component does no permission check itself.
// Layout uses MUI v7 Box flexbox (no Grid) per the project Frontend guideline.
export const ModelReplayPanel = ({ investigationId, onReplayed }: ModelReplayPanelProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { configs, loading } = useModelConfigs();

  const [selectedId, setSelectedId] = useState('');
  const [replaying, setReplaying] = useState(false);
  // Hard double-fire guard (idempotency-of-intent at the UI; the backend is the real guard).
  const inFlight = useRef(false);

  const isEmpty = !loading && configs.length === 0;

  const handleReplay = async () => {
    if (inFlight.current || replaying || !selectedId) return;
    inFlight.current = true;
    setReplaying(true);
    try {
      await apiClient.post(
        `/api/backend/ai-troubleshooting/investigations/${investigationId}/replay`,
        { modelConfigId: selectedId },
      );
      notify(translate('aiTroubleshooting.replay.success', { _: 'Replay complete' }), {
        type: 'success',
      });
      onReplayed?.();
    } catch (err) {
      const e = err as ApiError;
      const code = e?.errorCode;
      const status = e?.status;
      // Error mapping uses sub-plan 17's actual codes (REF-08 §4). The replay endpoint never
      // returns AI_INVALID_ANCHOR (its body is only { modelConfigId } and reuses the original's
      // already-validated anchor).
      if (code === 'AI_INVESTIGATION_NOT_FOUND' || (status === 404 && !code)) {
        notify(
          translate('aiTroubleshooting.replay.errors.investigationNotFound', {
            _: 'Investigation not found or out of scope.',
          }),
          { type: 'error' },
        );
      } else if (code === 'AI_MODEL_CONFIG_NOT_FOUND') {
        notify(
          translate('aiTroubleshooting.replay.errors.modelNotFound', {
            _: 'Selected model is no longer available; pick another model.',
          }),
          { type: 'error' },
        );
      } else if (code === 'AI_MODEL_UNAVAILABLE') {
        notify(
          translate('aiTroubleshooting.replay.errors.modelUnavailable', {
            _: 'The selected model is unavailable. Pick another model.',
          }),
          { type: 'error' },
        );
      } else if (code === 'VALIDATION_FAILED') {
        notify(e?.message || translate('aiTroubleshooting.replay.errors.validation', { _: 'Invalid request.' }), {
          type: 'error',
        });
      } else if (code === 'RATE_LIMIT_EXCEEDED' || status === 429) {
        const retry = e?.retryAfterSeconds;
        notify(
          retry
            ? translate('aiTroubleshooting.replay.errors.rateLimitedRetry', {
                _: 'Rate limit reached. Try again in %{seconds}s.',
                seconds: retry,
              })
            : e?.message ||
                translate('aiTroubleshooting.replay.errors.rateLimited', {
                  _: 'Rate limit reached. Try again shortly.',
                }),
          { type: 'error' },
        );
      } else {
        notify(
          e?.message ||
            translate('aiTroubleshooting.replay.errors.generic', { _: 'Replay failed.' }),
          { type: 'error' },
        );
      }
    } finally {
      inFlight.current = false;
      setReplaying(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {translate('aiTroubleshooting.replay.title', { _: 'Replay with model' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('aiTroubleshooting.replay.subtitle', {
            _: 'Re-run the same symptom and anchor against a different model to compare results.',
          })}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'flex-end' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {translate('aiTroubleshooting.replay.loadingModels', { _: 'Loading models...' })}
                </Typography>
              </Box>
            ) : (
              <TextField
                select
                fullWidth
                size="small"
                label={translate('aiTroubleshooting.replay.modelLabel', { _: 'Model' })}
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={isEmpty || replaying}
                helperText={
                  isEmpty
                    ? translate('aiTroubleshooting.replay.noModels', {
                        _: 'No active investigation model configured.',
                      })
                    : undefined
                }
                slotProps={{ htmlInput: { 'aria-label': translate('aiTroubleshooting.replay.modelLabel', { _: 'Model' }) } }}
              >
                {configs.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.displayName} ({c.provider})
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                replaying ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />
              }
              disabled={!selectedId || replaying || isEmpty}
              onClick={handleReplay}
            >
              {translate('aiTroubleshooting.replay.run', { _: 'Replay' })}
            </Button>
            {replaying && (
              <Typography variant="body2" color="text.secondary">
                {translate('aiTroubleshooting.replay.running', { _: 'Running replay...' })}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ModelReplayPanel;
